import { useAtom } from "jotai";
import { atomFamily, atomWithStorage } from "jotai/utils";
import { useCallback, useEffect, useMemo } from "react";
import type { LineDetails } from "../types";
import {
  selectedLineIdsAtom,
  linesByIdAtom,
  linesInFlightAtom,
} from "../atoms";
import { fetchLineDetails } from "../api/lines";
import { ignoreAbort } from "../utils/errors";
import { activeSelectionAtom } from "../atoms/activeSelection";

const lineParentStopsFamily = atomFamily((lineKey: string) =>
  atomWithStorage<string[]>(`ogt:line-parent-stops:${lineKey}`, [])
);

export function useLineParentStops(lineId: string) {
  const [selected, setSelected] = useAtom(
    lineParentStopsFamily(String(lineId))
  );
  return { selected, setSelected };
}

export function useSelectedLines() {
  const [lineIds, setLineIds] = useAtom(selectedLineIdsAtom);

  const [activeSelection, setActiveSelection] = useAtom(activeSelectionAtom);
  const activeLineId =
    activeSelection?.kind === "line" ? String(activeSelection.id) : null;

  const setActiveLineId = useCallback(
    (id: string | null) => {
      if (!id) setActiveSelection(null);
      else setActiveSelection({ kind: "line", id: String(id) });
    },
    [setActiveSelection]
  );

  const [linesById, setLinesById] = useAtom(linesByIdAtom);
  const [inFlight, setInFlight] = useAtom(linesInFlightAtom);

  const selectedLines = useMemo(
    () =>
      lineIds.map((id) => linesById.get(id)).filter(Boolean) as LineDetails[],
    [lineIds, linesById]
  );

  useEffect(() => {
    const missing = lineIds.filter(
      (id) => !linesById.get(id) && !inFlight.get(id)
    );
    if (missing.length === 0) return;

    missing.forEach((requestedId) => {
      const p = fetchLineDetails(requestedId)
        .then((det) => {
          const canonicalId = String(det.id);
          const fixed: LineDetails = { ...det, id: canonicalId };

          setLinesById((prev) => {
            const next = new Map(prev);
            next.set(requestedId, fixed);
            next.set(canonicalId, fixed);
            const alt = (det as any).LineNrReal;
            if (alt) next.set(String(alt), fixed);
            return next;
          });

          return fixed;
        })
        .catch(ignoreAbort)
        .finally(() => {
          setInFlight((prev) => {
            const next = new Map(prev);
            next.delete(requestedId);
            return next;
          });
        });

      setInFlight((prev) => {
        if (prev.has(requestedId)) return prev;
        const next = new Map(prev);
        next.set(requestedId, p as Promise<LineDetails>);
        return next;
      });
    });
  }, [lineIds, linesById, inFlight, setLinesById, setInFlight]);

  const getBestLineId = (line: LineDetails) => {
    return String(
      line.id ??
        (line as any).LineNrReal ??
        (line as any).lineNrReal ??
        (line as any).LineName ??
        (line as any).lineName ??
        (line as any).PublicLineNumber ??
        (line as any).publicLineNumber ??
        ""
    ).trim();
  };

  const addAndActivateLine = useCallback(
    (line: LineDetails | string) => {
      const id = typeof line === "string" ? String(line) : getBestLineId(line);

      setLinesById((prev) => {
        if (typeof line === "string") return prev;

        const fixed: LineDetails = { ...line, id };
        const next = new Map(prev);

        const aliases = [
          id,
          line.id,
          (line as any).LineNrReal,
          (line as any).lineNrReal,
          (line as any).LineName,
          (line as any).lineName,
          (line as any).PublicLineNumber,
          (line as any).publicLineNumber,
        ];

        for (const alias of aliases) {
          if (alias == null) continue;
          const key = String(alias).trim();
          if (key) next.set(key, fixed);
        }

        return next;
      });

      setLineIds((cur) => (cur.includes(id) ? cur : [id, ...cur]));
      setActiveLineId(id);
    },
    [setLineIds, setActiveLineId, setLinesById]
  );

  const addLinesToList = useCallback(
    (lines: LineDetails[]) => {
      const validLines = lines.filter((line) => getBestLineId(line));

      if (!validLines.length) return;

      const idsToAdd = validLines.map(getBestLineId);

      setLinesById((prev) => {
        const next = new Map(prev);

        for (const line of validLines) {
          const id = getBestLineId(line);
          const fixed: LineDetails = { ...line, id };

          next.set(id, fixed);

          const aliases = [
            line.id,
            (line as any).LineNrReal,
            (line as any).lineNrReal,
            (line as any).LineName,
            (line as any).PublicLineNumber,
            (line as any).publicLineNumber,
          ];

          for (const alias of aliases) {
            if (alias == null) continue;
            next.set(String(alias), fixed);
          }
        }

        return next;
      });

      setLineIds((cur) => {
        const next = [...cur];

        for (const id of idsToAdd) {
          if (!next.includes(id)) {
            next.unshift(id);
          }
        }

        return next;
      });
    },
    [setLineIds, setLinesById]
  );
  const removeLine = useCallback(
    (idRaw: string) => {
      const id = String(idRaw);
      setLineIds((cur) => cur.filter((x) => x !== id));

      if (
        activeSelection?.kind === "line" &&
        String(activeSelection.id) === id
      ) {
        setActiveSelection(null);
      }
    },
    [setLineIds, activeSelection, setActiveSelection]
  );

  const isLoading = useMemo(
    () => lineIds.some((id) => inFlight.has(id)),
    [lineIds, inFlight]
  );

  return {
    lineIds,
    setLineIds,
    selectedLines,
    activeLineId,
    setActiveLineId,
    addAndActivateLine,
    addLinesToList,
    removeLine,
    isLoading,
  };
}
